import React, { useState, useRef, useEffect } from 'react'
import { styles } from './index.sass'
import { Button, message, Card, Input, Spin as AntdSpin, Modal } from 'antd'
import axios from '../../api/axios_ml'
import { cleanHtmlContent } from '../../utils/htmlCleaner'
import RichTextEditor from '../../components/richTextEditor'

export const ApeironBlog = () => {
  const [topic, setTopic] = useState('')
  const [contentHtml, setContentHtml] = useState('')
  const [loading, setLoading] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [editedContent, setEditedContent] = useState('')
  const [progress, setProgress] = useState([])
  const editorContentRef = useRef('')
  const eventSourceRef = useRef(null)

  const handleGenerate = async (regenerate = false) => {
    if (!topic.trim()) {
      message.error('Please enter a topic for blog generation')
      return
    }

    setLoading(true)
    setProgress([])
    
    try {
      // Connect to SSE endpoint
      const url = `${process.env.REACT_APP_ML_API_ENDPOINT}/api/ml/generate-blog-stream`
      const eventSource = new EventSource(`${url}?topic=${encodeURIComponent(topic)}&regenerate=${true}`)
      eventSourceRef.current = eventSource

      eventSource.onmessage = (event) => {
        const data = JSON.parse(event.data)
        
        if (data.step !== undefined) {
          // Update progress
          setProgress(prev => [...prev, data])
        } else if (data.status === 'success') {
          // Blog generation completed
          const cleanedHtml = cleanHtmlContent(data.content_html)
          setContentHtml(cleanedHtml)
          message.success('Blog generated successfully!')
          eventSource.close()
          setLoading(false)
        } else if (data.status === 'error') {
          // Error occurred
          console.error('Error generating blog:', data.message)
          message.error(data.message || 'Failed to generate blog. Please try again.')
          eventSource.close()
          setLoading(false)
        }
      }

      eventSource.onerror = () => {
        console.error('Stream connection error')
        eventSource.close()
        setLoading(false)
      }

    } catch (err) {
      console.error('Error generating blog:', err)
      message.error(
        err.message || 'Failed to generate blog. Please try again.',
      )
      setLoading(false)
    }
  }

  const handleCopy = async () => {
    try {
      const cleanedHtml = cleanHtmlContent(contentHtml)

      if (typeof window !== 'undefined' && window.ClipboardItem) {
        const clipboardItem = new window.ClipboardItem({
          'text/html': new Blob([cleanedHtml], { type: 'text/html' }),
          'text/plain': new Blob([cleanedHtml], { type: 'text/plain' })
        })

        await navigator.clipboard.write([clipboardItem])
      } else {
        navigator.clipboard.writeText(cleanedHtml)
      }
      message.success('Copied to clipboard')
    } catch (err) {
      const cleanedHtml = cleanHtmlContent(contentHtml)
      navigator.clipboard.writeText(cleanedHtml)
      message.success('Copied to clipboard')
    }
  }

  const handleRegenerate = () => {
    Modal.confirm({
      title: 'Regenerate Blog',
      content: 'This will replace the current content. Continue?',
      onOk: () => handleGenerate(true)
    })
  }

  const handleEdit = () => {
    setEditedContent(contentHtml)
    editorContentRef.current = contentHtml
    setIsEditing(true)
  }

  const handleSave = () => {
    const finalContent = editorContentRef.current || editedContent
    setContentHtml(finalContent)
    setIsEditing(false)
    message.success('Changes saved successfully!')
  }

  const handleCancel = () => {
    setEditedContent(contentHtml)
    editorContentRef.current = ''
    setIsEditing(false)
    message.info('Changes discarded')
  }

  const handleEditorChange = (html) => {
    editorContentRef.current = html
  }

  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close()
      }
    }
  }, [])

  return (
    <div className={styles}>
      {loading && (
        <div className="loading-overlay">
          <div className="loading-popup">
            <AntdSpin size="large" style={{ marginBottom: '16px' }} />
            <div className="progress-steps">
              {progress.map((step, index) => (
                <div key={index} className={`progress-step step-${step.step} ${step.status}`}>
                  {step.message}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      <div className="container">
        <h2>Blog Content Generator</h2>
        <div className="input-section">
          <Input
            placeholder="Enter topic for blog generation"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            style={{ marginBottom: '16px' }}
          />
          <div className="actions">
            <Button type="primary" onClick={handleGenerate} loading={loading}>
              Generate Blog
            </Button>
            <Button
              onClick={handleRegenerate}
              disabled={!contentHtml || loading}
            >
              Regenerate
            </Button>
            <Button onClick={handleCopy} disabled={!contentHtml}>
              Copy Content
            </Button>
            <Button onClick={handleEdit} disabled={!contentHtml}>
              Edit Content
            </Button>
          </div>
        </div>
        {contentHtml && (
          <Card className="content-card">
            {isEditing ? (
              <div>
                <div style={{ marginBottom: '16px', textAlign: 'right' }}>
                  <Button onClick={handleCancel} style={{ marginRight: '8px' }}>
                    Cancel
                  </Button>
                  <Button type="primary" onClick={handleSave}>
                    Save Changes
                  </Button>
                </div>
                <RichTextEditor
                  value={editedContent}
                  onChange={handleEditorChange}
                  placeholder="Edit your blog content here..."
                />
              </div>
            ) : (
              <div
                className="content-preview"
                dangerouslySetInnerHTML={{ __html: contentHtml }}
              />
            )}
          </Card>
        )}
      </div>
    </div>
  )
}
